/* eslint-disable fp/no-mutating-methods */
import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PieChart } from '@mui/x-charts/PieChart'
import dayjs from 'dayjs'

import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Pagination from '@mui/material/Pagination'
import Select from '@mui/material/Select'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import theme from 'src/theme'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  fetchIBSC40Courses,
  selectIBSC40Courses,
  selectIBSC40CoursesStatus,
  fetchIBSC40Certification,
  selectIBSC40Certification,
  selectIBSC40CertificationStatus,
} from 'src/v3/profile/profile.slice'
import {
  CorrectIcon,
  DownloadIcon,
  IncorrectIcon,
  SearchIcon,
} from 'src/v3/core/icons'
import CelebrationIcon from '@mui/icons-material/Celebration'
import Subheader from 'src/v3/components/subheader'
import ConfirmAction from 'src/v3/components/confirm-action'
import { downloadFile } from 'src/v3/utilities/filesystem'
import Loading from 'src/v3/components/loading'

let extensions: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpeg',
  'image/png': 'png',
}

let getCertification = (id: string) =>
  axios
    .get<any>(`/api/v2/profile/ibsc40/certificate/export?id=${id}`)
    .then(pluckData)

export default (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let courses = useAppSelector(selectIBSC40Courses)
  let coursesStatus = useAppSelector(selectIBSC40CoursesStatus)
  let certification = useAppSelector(selectIBSC40Certification)
  let certificationStatus = useAppSelector(selectIBSC40CertificationStatus)

  let [dialog, setDialog] = useState<string>('')
  let [downloading, setDownloading] = useState<string>('')
  let [courseFilter, setCourseFilter] = useState<string>('All Courses')
  let [courseSearch, setCourseSearch] = useState<string>('')
  let [courseSort, setCourseSort] = useState<string>('name-az')
  let [page, setPage] = useState<number>(1)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let currentCert = certification?.certificates[0]
  let pastCerts = certification?.certificates.slice(
    certification?.status === 'Completed' ? 1 : 0,
  )

  let filteredCourses = courses
    .filter(
      (c) =>
        c.name.toLowerCase().includes(courseSearch.toLowerCase()) &&
        (courseFilter === 'All Courses' || c.status === courseFilter),
    )
    .sort((a, b) =>
      courseSort === 'name-az'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name),
    )
  let completed = courses.filter((c) => c.status === 'Complete')
  let percentComplete = Math.round((completed.length / courses.length) * 100)

  let chartWidth = 400

  useEffect(() => {
    dispatch(fetchIBSC40Courses())
    dispatch(fetchIBSC40Certification())
  }, [])

  useEffect(() => {
    if (filteredCourses.length <= (page - 1) * 10) {
      setPage(1)
    }
  }, [filteredCourses])

  let handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  return (
    <>
      <Typography variant="h1">IBSC 40-Hour Renewal</Typography>
      <Typography
        color="text.secondary"
        sx={{
          textAlign: 'center',
          mb: -1,
        }}
      >
        Monitor your progress in courses necessary for IBSC 40-hour renewal
      </Typography>
      <Subheader title="IBSC 40 Courses" />
      <Typography
        color="text.secondary"
        variant="subtitle2"
        sx={{
          mb: 2,
          ml: 1,
        }}
      >
        The FOAMfrat 40-hour IBSC Refresher includes the below individual
        courses that cannot be used towards the additional CE credits required
        for your IBSC renewal.
      </Typography>
      {certification?.status === 'Completed' && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CelebrationIcon fontSize="large" htmlColor="red" />
            <CelebrationIcon fontSize="large" htmlColor="orange" />
            <CelebrationIcon fontSize="large" htmlColor="yellow" />
            <Typography
              variant="h3"
              sx={{
                textAlign: 'center',
              }}
            >
              Congratulations!
            </Typography>
            <CelebrationIcon fontSize="large" htmlColor="limegreen" />
            <CelebrationIcon fontSize="large" htmlColor="dodgerblue" />
            <CelebrationIcon fontSize="large" htmlColor="blueviolet" />
          </Box>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
            }}
          >
            You have completed your IBSC 40-hour renewal!
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
            }}
          >
            {downloading === 'active' ? (
              <CircularProgress size="24px" />
            ) : (
              <Button
                onClick={() => {
                  setDownloading('active')
                  getCertification(currentCert?.id || '')
                    .then((response) =>
                      axios
                        .get(response, { responseType: 'blob' })
                        .catch(() => {
                          // eslint-disable-next-line fp/no-throw, no-throw-literal
                          throw 'File Not Found'
                        }),
                    )
                    .then(pluckData)
                    .then((response) =>
                      downloadFile(
                        response,
                        `IBSC-40.${extensions[response.type]}`,
                      ),
                    )
                    .then(() => setDownloading(''))
                    .catch((err) => {
                      setDownloading('')
                      setErrorMessage(
                        `IBSC 40 Certification Download Failed: ${err.response?.data || err}`,
                      )
                    })
                }}
              >
                Download your Certification
              </Button>
            )}
          </Typography>
          <Divider sx={{ mb: 3, mt: 1 }} />
        </Box>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 5 }}>
        <Box>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
            }}
          >
            Course Progress
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ mb: `-${chartWidth / 2}px` }}>
              <PieChart
                height={chartWidth}
                width={chartWidth}
                margin={{ right: 0, left: 0 }}
                hideLegend={true}
                series={[
                  {
                    innerRadius: chartWidth / 5,
                    startAngle: -90,
                    endAngle: 90,
                    data: [
                      {
                        id: 0,
                        value: completed.length,
                        label: 'Completed',
                        color: theme.palette.success.main,
                      },
                      {
                        id: 1,
                        value: courses.length - completed.length,
                        label: 'Incomplete',
                        color: theme.palette.error.main,
                      },
                    ],
                  },
                ]}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: chartWidth,
              }}
            >
              <Typography
                sx={{
                  textAlign: 'center',
                  flex: `0 0 ${chartWidth * 0.3}px`,
                }}
              >
                {completed.length} Completed
              </Typography>
              <Box sx={{ flex: '1 1 0' }}></Box>
              <Typography
                sx={{
                  textAlign: 'center',
                  flex: `0 0 ${chartWidth * 0.3}px`,
                }}
              >
                {courses.length - completed.length} Incomplete
              </Typography>
            </Box>
            <Box
              sx={{
                height: 0,
                width: chartWidth * 0.4,
                position: 'relative',
                bottom: chartWidth * 0.15, // .125
                // left: chartWidth * .3,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: 18, fontWeight: 'bold' }}>
                {percentComplete}% Complete
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              color="error"
              variant="outlined"
              size="small"
              onClick={() => {
                setDialog('reset')
              }}
            >
              Reset Progress
            </Button>
            <ConfirmAction
              executeAction={() =>
                axios
                  .post('/api/v2/profile/ibsc40/certification/reset', {
                    userID: certification?.certificates[0]?.userID,
                  })
                  .then(() => dispatch(fetchIBSC40Certification()))
                  .then(() => dispatch(fetchIBSC40Courses()))
                  .then(() =>
                    axios.post('/api/v2/profile/ibsc100/certification/reset', {
                      userID: certification?.certificates[0]?.userID,
                    }),
                  )
              }
              open={dialog}
              setOpen={setDialog}
              openText="reset"
              title="Confirm Progress Reset"
              text="Are you sure you want to reset progress for your IBSC 40 certification?"
              onFailure={(err) =>
                setErrorMessage(err.message || 'Error Resetting Certification')
              }
            />
          </Box>
          <Divider sx={{ my: 1 }} />
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              mb: '4px',
            }}
          >
            Filter Courses
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              color="primary"
              sx={{
                flexBasis: 130,
              }}
              variant={
                courseFilter === 'All Courses' ? 'contained' : 'outlined'
              }
              onClick={() => setCourseFilter('All Courses')}
            >
              All Courses
            </Button>
            <Button
              color="success"
              sx={{
                flexBasis: 130,
              }}
              variant={courseFilter === 'Complete' ? 'contained' : 'outlined'}
              onClick={() => setCourseFilter('Complete')}
            >
              Completed
            </Button>
            <Button
              color="error"
              sx={{
                flexBasis: 130,
              }}
              variant={courseFilter === 'Incomplete' ? 'contained' : 'outlined'}
              onClick={() => setCourseFilter('Incomplete')}
            >
              Incomplete
            </Button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 1,
              my: 2,
            }}
          >
            <TextField
              sx={{ width: 258 }}
              variant="outlined"
              size="small"
              label="Search Courses"
              placeholder="Course Name"
              name="course-search"
              type="text"
              value={courseSearch}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setCourseSearch(event.target.value)
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <FormControl sx={{ width: 140 }}>
              <InputLabel size="small">Sort</InputLabel>
              <Select
                value={courseSort}
                label="Sort"
                variant="outlined"
                size="small"
                onChange={(event) => setCourseSort(event.target.value)}
              >
                <MenuItem value={'name-az'}>Name: A-Z</MenuItem>
                <MenuItem value={'name-za'}>Name: Z-A</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Box sx={{ flex: '1 1 320px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filteredCourses.slice(10 * (page - 1), 10 * page).map((c) => (
              <Box
                key={c.id}
                sx={{
                  p: 0.5,
                  border: `1px solid ${theme.palette.primary.dark}`,
                  borderRadius: 20,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  maxWidth: 1024, // 680,
                  '&:hover': {
                    cursor: 'pointer',
                    bgcolor: theme.palette.background.paper,
                  },
                }}
                onClick={() => navigate(`/self-paced/course/${c.id}`)}
              >
                <Box sx={{ flex: '0 0 fit-content' }}>
                  <Avatar
                    src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${c.imageName}`}
                  />
                </Box>
                <Box sx={{ flex: '0 1 fit-content' }}>{c.name}</Box>
                <Box sx={{ flex: '1 0 1px' }}></Box>
                {c.status === 'Complete' ? (
                  <Chip
                    sx={{
                      width: 112,
                      mr: '5px',
                      color: 'black',
                    }}
                    variant="filled"
                    label="Completed"
                    icon={<CorrectIcon />}
                  />
                ) : (
                  <Chip
                    color="error"
                    sx={{
                      width: 112,
                      mr: '5px',
                      bgcolor: 'black',
                    }}
                    variant="outlined"
                    label="Incomplete"
                    icon={<IncorrectIcon />}
                  />
                )}
              </Box>
            ))}
          </Box>
          <Box sx={{ maxWidth: 1024 }}>
            {filteredCourses.length ? (
              <Pagination
                size="small"
                sx={{
                  paddingY: '5px',
                  display: 'flex',
                  justifyContent: 'center',
                }}
                count={Math.ceil(filteredCourses.length / 10)}
                page={page}
                onChange={handlePageChange}
                showFirstButton
                showLastButton
              />
            ) : (
              <Typography sx={{ ml: 5 }}>
                There are no courses matching these filters.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      {!!pastCerts?.length && (
        <>
          <Subheader title="Previously Obtained IBSC 40 Certificates" />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {pastCerts.map((cert) => (
              <Box key={cert.id} sx={{ p: 1, textAlign: 'center' }}>
                <Typography sx={{ mb: 1 }}>
                  Completed {dayjs(cert.createdTime).format('MM/DD/YYYY')}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    textAlign: 'center',
                  }}
                >
                  {downloading === cert.id ? (
                    <CircularProgress size="24px" />
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => {
                        setDownloading(cert.id)
                        getCertification(cert.id)
                          .then((response) =>
                            axios
                              .get(response, { responseType: 'blob' })
                              .catch(() => {
                                // eslint-disable-next-line fp/no-throw, no-throw-literal
                                throw 'File Not Found'
                              }),
                          )
                          .then(pluckData)
                          .then((response) =>
                            downloadFile(
                              response,
                              `IBSC-40.${extensions[response.type]}`,
                            ),
                          )
                          .then(() => setDownloading(''))
                          .catch((err) => {
                            setDownloading('')
                            setErrorMessage(
                              `IBSC 40 Certification Download Failed: ${err.response?.data || err}`,
                            )
                          })
                      }}
                    >
                      Download
                    </Button>
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading entities={[certificationStatus, coursesStatus]} />
    </>
  )
}
